import { useState } from "react";
import { useLoaderData } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import { api, ApiError } from "@/lib/api";
import {
  loadRetreatDetail,
  type PriceTier,
  type RetreatDetail as Retreat,
  type RetreatDetailResponse,
  type RetreatWaitlist,
} from "@/lib/retreats";
import Footer from "@/components/ui/Footer";
import Button from "@/components/ui/Button";
import TextInput from "@/components/ui/TextInput";

function formatPrice(cents: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    cents / 100,
  );
}

function formatDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.toLocaleDateString(undefined, { month: "long" })} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
  }
  return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`;
}

function WaitlistForm({ retreat, waitlist }: { retreat: Retreat; waitlist: RetreatWaitlist }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setStatus("idle");
    setMessage(null);
    try {
      await api.post(`/api/retreats/${retreat.slug}/waitlist`, {
        email,
        firstName: firstName || undefined,
      });
      setStatus("success");
      setMessage("You are on this retreat waitlist.");
      setFirstName("");
      setEmail("");
    } catch (e) {
      setStatus("error");
      setMessage(e instanceof Error ? e.message : "Waitlist signup failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      id="waitlist"
      onSubmit={submit}
      className="rounded-3xl p-6 shadow-lg"
      style={{ backgroundColor: "white", border: `1px solid ${colors.tan}30` }}
    >
      <p
        className="mb-2 text-xs uppercase"
        style={{
          fontFamily: fonts.body,
          fontWeight: 800,
          letterSpacing: "0.12em",
          color: colors.berryRose,
        }}
      >
        {waitlist.name}
      </p>
      <h2 className="mb-3 text-3xl" style={{ fontFamily: fonts.logo, color: colors.navy }}>
        Get first notice when spots open.
      </h2>
      <p className="mb-5 text-sm leading-relaxed" style={{ color: `${colors.navy}aa` }}>
        This list is only for {retreat.title}. You will not be mixed into a different retreat waitlist.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <TextInput
          label="First name"
          name="firstName"
          autoComplete="given-name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
        />
        <TextInput
          label="Email address"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>
      {message && (
        <p
          className="mt-3 text-sm"
          style={{
            color: status === "success" ? colors.forestGreen : colors.berryRose,
            fontFamily: fonts.body,
            fontWeight: 700,
          }}
        >
          {message}
        </p>
      )}
      <Button type="submit" disabled={busy} className="mt-4 w-full sm:w-auto">
        {busy ? "Joining..." : "Join Waitlist"}
      </Button>
    </form>
  );
}

const HEADING_NUM = { fontFamily: fonts.logo, letterSpacing: "0.5px" } as const;

function RateButton({
  tier,
  sub,
  active,
  onClick,
}: {
  tier: PriceTier;
  sub: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="p-5 rounded-xl text-left"
      style={{
        border: `2px solid ${active ? colors.berryRose : "rgba(0,0,0,0.12)"}`,
        backgroundColor: active ? `${colors.berryRose}1a` : "white",
        boxShadow: active ? "0 4px 12px rgba(0,0,0,0.08)" : "none",
        cursor: "pointer",
        transition: "border-color 120ms ease, background-color 120ms ease",
      }}
    >
      <div className="text-xs font-bold tracking-[0.2em] uppercase" style={{ opacity: 0.7 }}>
        {tier.name}
      </div>
      <div style={{ ...HEADING_NUM, fontSize: "32px", lineHeight: 1, color: colors.navy, marginTop: "4px" }}>
        {formatPrice(tier.priceCents)}
      </div>
      <div className="text-xs mt-1" style={{ opacity: 0.6 }}>
        {sub}
      </div>
    </button>
  );
}

// Interactive pricing builder ported from the legacy CBP retreat pages:
// pick a rate, optionally add the VIP upgrade, watch the total update, reserve.
// Drives the native Stripe checkout (Stripe collects name/email/payment).
function PricingBuilder({ retreat }: { retreat: Retreat }) {
  const tiers = retreat.priceTiers;
  const rateOptions = tiers
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => t.tierType === "full" || t.tierType === "deposit");
  const addonOption =
    tiers.map((t, i) => ({ t, i })).find(({ t }) => t.tierType === "addon") ?? null;

  const [selected, setSelectedRaw] = useState(rateOptions[0]?.i ?? 0);
  const [vip, setVip] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // coupon — discounts the base tier, or the VIP add-on when the code is
  // addon-scoped (matches the server)
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState<
    {
      code: string;
      type: "percentage" | "fixed" | "final_price";
      value: number;
      appliesToTierType: "any" | "deposit" | "full" | "addon";
      description: string;
    } | null
  >(null);
  const [couponMsg, setCouponMsg] = useState<string | null>(null);
  const [couponBusy, setCouponBusy] = useState(false);

  const selectedTier = tiers[selected];
  const isDeposit = selectedTier?.tierType === "deposit";
  const effectiveVip = !isDeposit && vip && !!addonOption;
  const base = selectedTier?.priceCents ?? 0;
  const addonPrice = effectiveVip && addonOption ? addonOption.t.priceCents : 0;
  const discountTarget = applied?.appliesToTierType === "addon" ? addonPrice : base;
  const discount = applied
    ? applied.type === "percentage"
      ? Math.round(discountTarget * (applied.value / 100))
      : applied.type === "final_price"
        ? Math.max(0, discountTarget - applied.value)
        : Math.min(applied.value, discountTarget)
    : 0;
  const total = Math.max(0, base + addonPrice - discount);

  // switching rates can invalidate a tier-scoped coupon — clear it
  function selectRate(i: number) {
    setSelectedRaw(i);
    setApplied(null);
    setCouponMsg(null);
  }

  // removing the VIP upgrade invalidates an addon-scoped coupon
  function toggleVip() {
    const next = !vip;
    setVip(next);
    if (!next && applied?.appliesToTierType === "addon") {
      setApplied(null);
      setCouponMsg(null);
    }
  }

  async function applyCoupon() {
    const code = coupon.trim();
    if (!code) return;
    setCouponBusy(true);
    setCouponMsg(null);
    try {
      const r = await api.post<{
        code: string;
        type: "percentage" | "fixed" | "final_price";
        value: number;
        appliesToTierType: "any" | "deposit" | "full" | "addon";
        description: string;
      }>("/api/coupons/validate", {
        code,
        scope: "retreats",
        retreatId: retreat.id,
        tierTypes: [
          ...(selectedTier ? [selectedTier.tierType] : []),
          ...(effectiveVip ? ["addon"] : []),
        ],
        subtotalCents: base + addonPrice,
      });
      setApplied({
        code: r.code,
        type: r.type,
        value: r.value,
        appliesToTierType: r.appliesToTierType,
        description: r.description,
      });
    } catch (e) {
      setApplied(null);
      setCouponMsg(e instanceof Error ? e.message : "Invalid code");
    } finally {
      setCouponBusy(false);
    }
  }

  function tierSub(t: PriceTier): string {
    if (t.tierType === "deposit") {
      return t.balanceDueCents ? `then ${formatPrice(t.balanceDueCents)} balance` : "reserve your spot";
    }
    const m = t.description?.match(/save \$[\d,]+/i);
    return m ? m[0] : "standard rate";
  }

  async function reserve() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.post<{ url: string }>("/api/retreats/checkout", {
        retreatId: retreat.id,
        tierIndex: selected,
        ...(effectiveVip && addonOption ? { addonTierIndex: addonOption.i } : {}),
        ...(applied ? { couponCode: applied.code } : {}),
      });
      window.location.href = r.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Checkout failed");
      setBusy(false);
    }
  }

  return (
    <div
      id="reserve"
      className="rounded-3xl p-8 shadow-xl"
      style={{ width: "100%", maxWidth: "640px", margin: "0 auto", backgroundColor: "white" }}
    >
      {/* Step 1 — rate */}
      <div className="mb-8">
        <div className="text-xs tracking-[0.3em] font-bold mb-3" style={{ opacity: 0.6 }}>
          STEP 1 · CHOOSE YOUR RATE
        </div>
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: `repeat(${Math.min(rateOptions.length, 3)}, minmax(0, 1fr))` }}
        >
          {rateOptions.map(({ t, i }) => (
            <RateButton
              key={i}
              tier={t}
              sub={tierSub(t)}
              active={selected === i}
              onClick={() => selectRate(i)}
            />
          ))}
        </div>
      </div>

      {/* Step 2 — optional VIP upgrade (hidden for deposit) */}
      {addonOption && !isDeposit && (
        <div className="mb-8">
          <div className="text-xs tracking-[0.3em] font-bold mb-3" style={{ opacity: 0.6 }}>
            STEP 2 · OPTIONAL UPGRADE
          </div>
          <button
            type="button"
            onClick={toggleVip}
            className="w-full p-5 rounded-xl text-left flex items-start gap-4"
            style={{
              border: `2px solid ${vip ? colors.berryRose : "rgba(0,0,0,0.12)"}`,
              backgroundColor: vip ? `${colors.berryRose}1a` : "white",
              boxShadow: vip ? "0 4px 12px rgba(0,0,0,0.08)" : "none",
              cursor: "pointer",
              transition: "border-color 120ms ease, background-color 120ms ease",
            }}
          >
            <div
              className="flex-shrink-0 flex items-center justify-center"
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "6px",
                border: `2px solid ${vip ? colors.berryRose : "rgba(0,0,0,0.3)"}`,
                backgroundColor: vip ? colors.berryRose : "transparent",
                color: "white",
                fontWeight: "bold",
                marginTop: "4px",
              }}
            >
              {vip ? "✓" : ""}
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div className="font-bold" style={{ fontSize: "16px", color: colors.navy }}>
                  {addonOption.t.name}
                </div>
                <div style={{ ...HEADING_NUM, fontSize: "24px", color: colors.navy }}>
                  +{formatPrice(addonOption.t.priceCents)}
                </div>
              </div>
              {addonOption.t.description && (
                <div style={{ fontSize: "14px", opacity: 0.7, marginTop: "4px", color: colors.navy }}>
                  {addonOption.t.description}
                </div>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Discount code */}
      <div className="mb-6">
        <div className="text-xs tracking-[0.3em] font-bold mb-3" style={{ opacity: 0.6 }}>
          DISCOUNT CODE
        </div>
        {applied ? (
          <div
            className="flex items-center justify-between p-3 rounded-xl"
            style={{
              backgroundColor: `${colors.forestGreen}14`,
              border: `1px solid ${colors.forestGreen}55`,
            }}
          >
            <span style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy }}>
              ✓ {applied.code} — {applied.description}
            </span>
            <button
              type="button"
              onClick={() => {
                setApplied(null);
                setCoupon("");
                setCouponMsg(null);
              }}
              style={{ color: colors.berryRose, fontWeight: 700, cursor: "pointer", fontSize: 13 }}
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <input
              value={coupon}
              onChange={(e) => setCoupon(e.target.value)}
              placeholder="Enter code"
              className="flex-1 p-3 rounded-xl"
              style={{ border: `1px solid ${colors.tan}55`, fontFamily: fonts.body, color: colors.navy }}
            />
            <button
              type="button"
              onClick={applyCoupon}
              disabled={couponBusy || !coupon.trim()}
              className="px-5 rounded-xl"
              style={{
                border: `2px solid ${colors.navy}`,
                color: colors.navy,
                fontFamily: fonts.button,
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: 12,
                letterSpacing: "0.05em",
                cursor: couponBusy || !coupon.trim() ? "default" : "pointer",
                opacity: couponBusy || !coupon.trim() ? 0.5 : 1,
              }}
            >
              {couponBusy ? "…" : "Apply"}
            </button>
          </div>
        )}
        {couponMsg && (
          <p className="mt-2" style={{ color: colors.berryRose, fontSize: 13, fontWeight: 600 }}>
            {couponMsg}
          </p>
        )}
      </div>

      {/* Total + CTA */}
      <div className="pt-6" style={{ borderTop: "2px dashed rgba(0,0,0,0.1)" }}>
        {discount > 0 && (
          <div
            className="flex items-baseline justify-between mb-2"
            style={{ color: colors.forestGreen, fontFamily: fonts.body, fontWeight: 700 }}
          >
            <div className="text-xs tracking-[0.2em]">DISCOUNT · {applied?.code}</div>
            <div>−{formatPrice(discount)}</div>
          </div>
        )}
        <div className="flex items-baseline justify-between mb-5">
          <div className="text-xs tracking-[0.3em] font-bold" style={{ opacity: 0.6 }}>
            YOUR TOTAL
          </div>
          <div style={{ ...HEADING_NUM, fontSize: "56px", lineHeight: 1, color: colors.navy }}>
            {formatPrice(total)}
          </div>
        </div>

        {err && (
          <p className="mb-3" style={{ color: colors.berryRose, fontSize: 14, fontWeight: 600 }}>
            {err}
          </p>
        )}

        <button
          type="button"
          onClick={reserve}
          disabled={busy}
          className="w-full py-4 rounded-full text-white"
          style={{
            backgroundColor: colors.berryRose,
            fontFamily: fonts.button,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            fontSize: 14,
            cursor: busy ? "default" : "pointer",
            opacity: busy ? 0.8 : 1,
          }}
        >
          {busy ? "Redirecting to Stripe…" : `Reserve at ${formatPrice(total)}`}
        </button>

        {isDeposit && selectedTier?.balanceDueCents ? (
          <p style={{ fontSize: "13px", opacity: 0.75, textAlign: "center", marginTop: "8px", marginBottom: 0 }}>
            Pay {formatPrice(base)} now to hold your spot; {formatPrice(selectedTier.balanceDueCents)} balance due before the retreat.
          </p>
        ) : null}
      </div>
    </div>
  );
}

// Custom on-brand FAQ accordion. Smooth height + fade via framer-motion,
// replacing the native <details>/<summary>.
function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="mb-3 overflow-hidden rounded-2xl"
      style={{
        backgroundColor: "white",
        border: `1px solid ${open ? `${colors.berryRose}55` : `${colors.tan}30`}`,
        boxShadow: open ? "0 8px 22px rgba(8,45,61,0.07)" : "none",
        transition: "border-color 200ms ease, box-shadow 200ms ease",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
        style={{ fontFamily: fonts.body, fontWeight: 700, color: colors.navy, cursor: "pointer" }}
      >
        <span className="text-base sm:text-lg">{question}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="flex-shrink-0"
          style={{ color: colors.berryRose }}
        >
          <ChevronDown size={20} strokeWidth={2.5} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="answer"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
            style={{ overflow: "hidden" }}
          >
            <p
              className="whitespace-pre-line px-5 pb-5 leading-relaxed"
              style={{ fontFamily: fonts.body, color: `${colors.navy}cc` }}
            >
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Each content section fades/rises in as it enters the viewport — sections
// visible on load animate immediately, mirroring the hero's entrance.
const sectionReveal = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] as const },
  },
};

function RevealSection({
  children,
  className,
  style,
  id,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
}) {
  return (
    <motion.section
      id={id}
      className={className}
      style={style}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={sectionReveal}
    >
      {children}
    </motion.section>
  );
}

export async function retreatDetailLoader({ params }: { params: { slug?: string } }) {
  if (!params.slug) {
    throw new Response("Retreat not found", { status: 404, statusText: "Retreat not found" });
  }

  try {
    return await loadRetreatDetail(params.slug);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      throw new Response("Retreat not found", { status: 404, statusText: "Retreat not found" });
    }
    throw error;
  }
}

export function RetreatDetail() {
  const { retreat, waitlist } = useLoaderData() as RetreatDetailResponse;

  const dates = formatDateRange(retreat.startDate, retreat.endDate);
  const isOpen = retreat.status === "open";

  return (
    <div className="min-h-dvh" style={{ backgroundColor: colors.cream }}>

      <motion.section
        className="px-6 pt-28 pb-16 text-center"
        initial="hidden"
        animate="visible"
        variants={stagger}
      >
        <motion.p
          variants={fadeUp}
          custom={0}
          className="text-xs tracking-[0.18em] uppercase mb-3"
          style={{ fontFamily: fonts.body, fontWeight: 700, color: `${colors.navy}88` }}
        >
          {dates} · {retreat.location}
          {retreat.venue && ` · ${retreat.venue}`}
        </motion.p>
        <motion.h1
          variants={fadeUp}
          custom={1}
          className="text-5xl md:text-6xl mb-4"
          style={{ fontFamily: fonts.logo, color: colors.navy }}
        >
          {retreat.title}
        </motion.h1>
        {retreat.subtitle && (
          <motion.p
            variants={fadeUp}
            custom={2}
            className="text-xl mb-4"
            style={{ fontFamily: fonts.heading, color: colors.berryRose }}
          >
            {retreat.subtitle}
          </motion.p>
        )}
        {retreat.tagline && (
          <motion.p
            variants={fadeUp}
            custom={3}
            className="max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: fonts.body, color: `${colors.navy}cc` }}
          >
            {retreat.tagline}
          </motion.p>
        )}
        {isOpen && (
          <motion.div variants={fadeUp} custom={4} className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href="#reserve"
              className="inline-block px-8 py-3 rounded-full"
              style={{
                backgroundColor: colors.berryRose,
                color: "white",
                fontFamily: fonts.body,
                fontWeight: 700,
              }}
            >
              Reserve Your Spot
            </a>
            {waitlist && (
              <a
                href="#waitlist"
                className="inline-block px-8 py-3 rounded-full border-2"
                style={{
                  borderColor: colors.berryRose,
                  color: colors.berryRose,
                  fontFamily: fonts.body,
                  fontWeight: 700,
                }}
              >
                Join Waitlist
              </a>
            )}
          </motion.div>
        )}
        {retreat.status === "sold-out" && (
          <motion.div variants={fadeUp} custom={4} className="mt-4">
            <p style={{ color: colors.berryRose, fontWeight: 700 }}>SOLD OUT</p>
            {waitlist && (
              <a href="#waitlist" style={{ color: colors.berryRose, fontWeight: 700 }}>
                Join the retreat waitlist →
              </a>
            )}
          </motion.div>
        )}
      </motion.section>

      {waitlist && (
        <RevealSection className="px-6 pb-16">
          <div className="max-w-2xl mx-auto">
            <WaitlistForm retreat={retreat} waitlist={waitlist} />
          </div>
        </RevealSection>
      )}

      {retreat.aboutBodyMd && (
        <RevealSection className="px-6 py-16" style={{ backgroundColor: colors.navy, color: colors.cream }}>
          <div className="max-w-3xl mx-auto whitespace-pre-line" style={{ fontFamily: fonts.body }}>
            <h2 className="text-4xl mb-6" style={{ fontFamily: fonts.logo }}>About this Retreat</h2>
            {retreat.aboutBodyMd}
          </div>
        </RevealSection>
      )}

      {retreat.details.length > 0 && (
        <RevealSection className="px-6 py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl mb-6" style={{ fontFamily: fonts.logo, color: colors.navy }}>
              Quick Details
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {retreat.details.map((d, i) => (
                <li key={i} className="flex gap-2 items-start" style={{ fontFamily: fonts.body, color: colors.navy }}>
                  <span className="text-xl">{d.icon ?? "•"}</span>
                  <span>{d.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </RevealSection>
      )}

      {retreat.projects.length > 0 && (
        <RevealSection className="px-6 py-16" style={{ backgroundColor: colors.softLilac }}>
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl mb-6" style={{ fontFamily: fonts.logo, color: colors.navy }}>
              Retreat Project
            </h2>
            {retreat.projects.map((p, i) => (
              <div
                key={i}
                className="mb-6 p-6 rounded-2xl"
                style={{ backgroundColor: "white", border: `1px solid ${colors.tan}30` }}
              >
                <h3 className="text-2xl mb-3" style={{ fontFamily: fonts.heading, color: colors.berryRose }}>
                  {p.name}
                </h3>
                {p.description && (
                  <p className="mb-4 leading-relaxed" style={{ fontFamily: fonts.body, color: `${colors.navy}cc` }}>
                    {p.description}
                  </p>
                )}
                {p.kitContents && p.kitContents.length > 0 && (
                  <ul className="grid gap-2">
                    {p.kitContents.map((k, j) => (
                      <li key={j} className="flex gap-2 items-start" style={{ fontFamily: fonts.body, color: colors.navy }}>
                        <span style={{ color: colors.berryRose }}>✦</span>
                        <span>{k.item}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </RevealSection>
      )}

      {retreat.included.length > 0 && (
        <RevealSection className="px-6 py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl mb-6" style={{ fontFamily: fonts.logo, color: colors.navy }}>
              What's Included
            </h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {retreat.included.map((it, i) => (
                <li key={i} className="flex gap-2 items-start" style={{ fontFamily: fonts.body, color: colors.navy }}>
                  <span className="text-xl">{it.icon ?? "•"}</span>
                  <span>{it.label}</span>
                </li>
              ))}
            </ul>
          </div>
        </RevealSection>
      )}

      {retreat.priceTiers.length > 0 && isOpen && (
        <RevealSection id="build" className="px-6 py-16" style={{ backgroundColor: colors.softLilac, scrollMarginTop: "24px" }}>
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl mb-2 text-center" style={{ fontFamily: fonts.logo, color: colors.navy }}>
              Build Your Retreat
            </h2>
            <p className="text-center mb-8" style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>
              Pick your rate, add the optional VIP upgrade, and see your total update live.
            </p>
            <PricingBuilder retreat={retreat} />
          </div>
        </RevealSection>
      )}

      {retreat.travelInfo && (
        <RevealSection className="px-6 py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl mb-4" style={{ fontFamily: fonts.logo, color: colors.navy }}>
              Travel Information
            </h2>
            <p className="leading-relaxed whitespace-pre-line" style={{ fontFamily: fonts.body, color: `${colors.navy}cc` }}>
              {retreat.travelInfo}
            </p>
          </div>
        </RevealSection>
      )}

      {retreat.sponsors.length > 0 && (
        <RevealSection className="px-6 py-16" style={{ backgroundColor: colors.softLilac }}>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl mb-8" style={{ fontFamily: fonts.logo, color: colors.navy }}>
              Sponsored By
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {retreat.sponsors.map((s, i) => {
                const inner = s.logoUrl ? (
                  <span
                    className="inline-flex items-center justify-center px-8 py-5 rounded-2xl"
                    style={{ backgroundColor: "white", border: `1px solid ${colors.tan}30` }}
                  >
                    <img
                      src={s.logoUrl}
                      alt={s.name}
                      style={{ maxHeight: "72px", width: "auto", display: "block" }}
                    />
                  </span>
                ) : (
                  <span
                    className="inline-block px-6 py-3 rounded-full"
                    style={{
                      fontFamily: fonts.heading,
                      fontWeight: 700,
                      color: colors.navy,
                      backgroundColor: "white",
                      border: `1px solid ${colors.tan}30`,
                    }}
                  >
                    {s.name}
                  </span>
                );
                return s.websiteUrl ? (
                  <a key={i} href={s.websiteUrl} target="_blank" rel="noreferrer">
                    {inner}
                  </a>
                ) : (
                  <span key={i}>{inner}</span>
                );
              })}
            </div>
          </div>
        </RevealSection>
      )}

      {retreat.faq.length > 0 && (
        <RevealSection className="px-6 py-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl mb-6" style={{ fontFamily: fonts.logo, color: colors.navy }}>
              FAQ
            </h2>
            {retreat.faq.map((f, i) => (
              <FaqItem key={i} question={f.question} answer={f.answer} />
            ))}
          </div>
        </RevealSection>
      )}

      <Footer />
    </div>
  );
}
